import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProductForCheckout } from '../config/stripe';
import { uploadImage } from '../services/uploadService';
import './ProductDetailPage.css';

// Import cake variation images
import cakeChocolate from '../assets/images/3 milk cake with chocolate.jpg';
import cakeCaramel from '../assets/images/3 milk cake with caramel.jpeg';
import cakeTiramisu from '../assets/images/3 milk cake with tiramisu.jpeg';
import cakeLotus from '../assets/images/3 milk cake with lotus.jpg';
import cakeBuenos from '../assets/images/3 milk cake with buenos.jpg';
import cakeOreos from '../assets/images/oreo.jpeg';
import cakePistachio from '../assets/images/3 milk cake with pistachio.jpg';
import cakeFront from '../assets/images/cake-front.png';
import chocolateCakeSpecial from '../assets/images/3 milk cake chocolate cake.jpg';
import impossibleCake from '../assets/images/impossible cake.jpg';
import burrera from '../assets/images/burrera.jpg';
import carrotCake from '../assets/images/carrot cake.jpeg';
import cupcakesMix from '../assets/images/cupcakes mix.jpg';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [collectionDay, setCollectionDay] = useState('');

  const [showCartNotification, setShowCartNotification] = useState(false);
  const [cartItemCount, setCartItemCount] = useState(0);

  // Personalization states
  const [isPersonalized, setIsPersonalized] = useState(false);
  const [personalizationMessage, setPersonalizationMessage] = useState('');
  const [personalizationImages, setPersonalizationImages] = useState([]);

  // Flavor selection state
  const [selectedFlavor, setSelectedFlavor] = useState(null);

  // Size selection state
  const [selectedSize, setSelectedSize] = useState(null);

  // Scroll to top when component mounts or productId changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  // Load cart count on component mount
  useEffect(() => {
    const updateCartCount = () => {
      const savedCartItems = localStorage.getItem('panelaCrumbsCart');
      if (savedCartItems) {
        const items = JSON.parse(savedCartItems);
        const count = items.reduce((sum, item) => sum + item.quantity, 0);
        setCartItemCount(count);
      }
    };

    updateCartCount();

    // Listen for cart updates from other pages/components
    window.addEventListener('cartUpdated', updateCartCount);

    return () => {
      window.removeEventListener('cartUpdated', updateCartCount);
    };
  }, []);

  // Generate collection dates (only Friday, Saturday, Sunday for the next 3 weeks)
  const getCollectionDates = () => {
    const dates = [];
    const today = new Date();
    let currentWeek = null;

    // Closure period (inclusive): no collections from 22 December 2026 to 10 January 2027
    const closureStart = '2026-12-22';
    const closureEnd = '2027-01-10';
    const toLocalISODate = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    // Look ahead up to 21 days (3 weeks)
    for (let i = 1; i <= 21; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);

      const dayOfWeek = date.getDay(); // 0 = Sunday, 5 = Friday, 6 = Saturday

      // Skip dates during closure period
      const localDate = toLocalISODate(date);
      if (localDate >= closureStart && localDate <= closureEnd) {
        continue;
      }

      // Only include Friday (5), Saturday (6), and Sunday (0)
      if (dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0) {
        const dayName = date.toLocaleDateString('en-GB', { weekday: 'long' });
        const dateString = date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });

        // Get week number to group dates
        const weekNumber = Math.ceil((i + today.getDay()) / 7);

        // Add separator between weeks (before first day of new week)
        if (currentWeek !== null && currentWeek !== weekNumber) {
          dates.push({
            value: `separator-${weekNumber}`,
            label: '─────────────────────',
            isSeparator: true,
            disabled: true
          });
        }
        currentWeek = weekNumber;

        dates.push({
          value: date.toISOString().split('T')[0], // YYYY-MM-DD format
          label: `${dayName}, ${dateString}`,
          date: date
        });
      }
    }

    return dates;
  };

  const collectionDates = getCollectionDates();

  // Get Stripe product data if available
  const stripeProduct = getProductForCheckout(productId);

  // Handle add to cart
  const handleAddToCart = () => {
    if (!collectionDay) {
      alert('Please select a collection date');
      return;
    }

    const selectedDate = collectionDates.find(date => date.value === collectionDay && !date.isSeparator);
    const item = {
      ...totalPrice,
      collectionDate: selectedDate?.label,
      collectionDateValue: collectionDay,
      productName: product.name,
      productImage: product.image,
      id: Date.now(), // Simple ID for demo
      // Size selection
      selectedSize: selectedSize ? {
        id: selectedSize.id,
        dimensions: selectedSize.dimensions,
        portions: selectedSize.portions,
        price: selectedSize.price
      } : null,
      // Flavor selection
      selectedFlavor: selectedFlavor ? {
        id: selectedFlavor.id,
        name: selectedFlavor.name,
        price: selectedFlavor.price
      } : null,
      // Personalization data
      isPersonalized,
      personalizationMessage: isPersonalized ? personalizationMessage : '',
      personalizationImages: isPersonalized ? personalizationImages.map(img => ({
        id: img.id,
        name: img.name,
        url: img.url || img.preview, // Use Vercel Blob URL if available, fallback to preview
        preview: img.preview
      })) : []
    };

    // Get existing cart items from localStorage
    const existingCartItems = localStorage.getItem('panelaCrumbsCart');
    const cartItems = existingCartItems ? JSON.parse(existingCartItems) : [];

    // Add new item to cart
    const updatedCartItems = [...cartItems, item];

    // Save to localStorage
    localStorage.setItem('panelaCrumbsCart', JSON.stringify(updatedCartItems));

    // Update cart count
    const newCount = updatedCartItems.reduce((sum, item) => sum + item.quantity, 0);
    setCartItemCount(newCount);

    // Dispatch cart update event
    window.dispatchEvent(new Event('cartUpdated'));

    // Show notification
    setShowCartNotification(true);

    // Hide notification after 4 seconds
    setTimeout(() => {
      setShowCartNotification(false);
    }, 4000);
  };

  // Handle continue shopping
  const handleContinueShopping = () => {
    setShowCartNotification(false);
    navigate('/menu');
  };

  // Handle view cart
  const handleViewCart = () => {
    setShowCartNotification(false);
    navigate('/cart'); // Navigate to cart page
  };

  // Handle personalization checkbox
  const handlePersonalizationToggle = (e) => {
    const checked = e.target.checked;
    setIsPersonalized(checked);
    if (!checked) {
      setPersonalizationMessage('');
      setPersonalizationImages([]);
    }
  };

  // Handle file upload with Vercel Blob
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + personalizationImages.length > 3) {
      alert('Maximum 3 images allowed');
      return;
    }

    // Show uploading state
    const uploadingImages = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      preview: URL.createObjectURL(file),
      uploading: true
    }));

    setPersonalizationImages(prev => [...prev, ...uploadingImages]);

    // Upload to Vercel Blob
    try {
      const uploadPromises = files.map(async (file, index) => {
        const url = await uploadImage(file, 'personalization');
        return {
          id: uploadingImages[index].id,
          name: file.name,
          url: url,
          preview: uploadingImages[index].preview,
          uploading: false
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);

      // Update with uploaded URLs
      setPersonalizationImages(prev =>
        prev.map(img => {
          const uploaded = uploadedImages.find(u => u.id === img.id);
          return uploaded || img;
        })
      );

    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload images. Please try again.');
      // Remove failed uploads
      setPersonalizationImages(prev =>
        prev.filter(img => !uploadingImages.find(u => u.id === img.id))
      );
    }
  };

  // Remove uploaded image
  const removeImage = (imageId) => {
    setPersonalizationImages(prev => {
      const updated = prev.filter(img => img.id !== imageId);
      // Clean up object URLs to prevent memory leaks
      prev.forEach(img => {
        if (img.id === imageId) {
          URL.revokeObjectURL(img.preview);
        }
      });
      return updated;
    });
  };

  // Calculate total price based on quantity, selected size, and selected flavor
  const totalPrice = stripeProduct ? (() => {
    let priceInCents = stripeProduct.priceInCents;

    // If a size is selected, use its price instead
    if (selectedSize && selectedSize.price) {
      // Parse the size price (e.g., "£8" or "£45")
      const sizePriceMatch = selectedSize.price.match(/[\d.]+/);
      if (sizePriceMatch) {
        priceInCents = Math.round(parseFloat(sizePriceMatch[0]) * 100);
      }
    }
    // If a flavor is selected, use its price instead
    else if (selectedFlavor && selectedFlavor.price) {
      // Parse the flavor price (e.g., "£38" or "£40")
      const flavorPriceMatch = selectedFlavor.price.match(/[\d.]+/);
      if (flavorPriceMatch) {
        priceInCents = Math.round(parseFloat(flavorPriceMatch[0]) * 100);
      }
    }

    return {
      ...stripeProduct,
      price: `£${((priceInCents * quantity) / 100).toFixed(2)}`,
      priceInCents: priceInCents * quantity,
      quantity: quantity
    };
  })() : null;

  // Product data - moved from MenuPage for better organization
  const productData = {
    'tres-leches-traditional': {
      name: "Traditional Tres leches cake",
      price: "£8.50",
      image: cakeFront,
      description: "One of Venezuela's most beloved desserts, Tres Leches is a light sponge cake soaked in a rich blend of three types of milk.",
      sizes: [
        {
          id: 'small',
          dimensions: '14cm x 12cm',
          portions: '2 portions',
          price: '£8.50'
        },
        {
          id: 'medium',
          dimensions: '25cm x 25cm',
          portions: '9 portions',
          price: '£45'
        },
        {
          id: 'large',
          dimensions: '32cm x 26cm',
          portions: '12 portions',
          price: '£60'
        }
      ],
      highlights: [
        "Moist and creamy inside",
        "Light and airy sponge",
        "Subtle notes of vanilla and lemon zest",
        "Often served cold, making it refreshing and indulgent"
      ],
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, sugar, condensed MILK, evaporated MILK, double cream (MILK), vanilla.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring.",
          "Please consume within 3 days of opening the product."
        ]
      },
      category: "signature"
    },
    'tres-leches-chocolate': {
      name: "Chocolate Tres leches cake",
      price: "£9",
      image: cakeChocolate,
      description: "Our traditional tres leches with a rich chocolate twist that chocolate lovers adore.",
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, sugar, condensed MILK, evaporated MILK, double cream (MILK), vanilla.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring.",
          "Please consume within 3 days of opening the product."
        ]
      },
      category: "signature"
    },
    'tres-leches-caramel': {
      name: "Caramel Tres leches cake",
      price: "£9",
      image: cakeCaramel,
      description: "Traditional tres leches enhanced with smooth, rich caramel for an indulgent experience.",
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, sugar, condensed MILK, evaporated MILK, double cream (MILK), vanilla.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring.",
          "Please consume within 3 days of opening the product."
        ]
      },
      category: "signature"
    },
    'tres-leches-lotus': {
      name: "Lotus Tres leches cake",
      price: "£9",
      image: cakeLotus,
      description: "Our bestselling tres leches featuring the beloved Lotus Biscoff flavor that customers can't get enough of.",
      isBestSeller: true,
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, sugar, condensed MILK, evaporated MILK, double cream (MILK), vanilla.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring.",
          "Please consume within 3 days of opening the product."
        ]
      },
      category: "signature"
    },
    'chocolate-tres-leches': {
      name: "Chocolate Tres leches cake",
      price: "£45",
      image: chocolateCakeSpecial,
      description: "Chocolate Sponge with chocolate cream at the top and chocolate topping",
      isSpecial: true,
      specialTitle: "For ChocoLovers Special",
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, sugar, condensed MILK, evaporated MILK, double cream (MILK), vanilla.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring.",
          "Please consume within 3 days of opening the product."
        ]
      },
      category: "special"
    },
    'impossible-cake': {
      name: "Impossible Cake 8\"",
      price: "£42",
      image: impossibleCake,
      description: "Also known as \"Chocoflan\" this layered treat is baked together in a single pan, creating a magical inversion where the flan rises above the cake. Finished with a glossy caramel topping, it's a showstopper for any celebration and a beloved classic in Latin American cuisine.",
      allergens: {
        ingredients: "EGGS, WHEAT flour, butter (MILK), sugar, condensed MILK, vanilla, SOY.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring."
        ]
      },
      category: "specialty"
    },
    'carrot-cake': {
      name: "Carrot Cake 8\"",
      price: "£46",
      image: carrotCake,
      description: "Our Carrot Cake is a moist, spiced delight made with freshly grated carrots, warm cinnamon, and a rich cream cheese frosting. It's a comforting classic that's perfect for any season—especially when you want something sweet, nutty, and satisfying.",
      allergens: {
        ingredients: "EGGS, WHEAT flour, sunflower oil, sugar, cinnamon, carrot, cream cheese (MILK), NUTS.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring."
        ]
      },
      category: "specialty"
    },
    'burrera': {
      name: "Burrera",
      price: "From £38",
      image: burrera,
      description: "Burrera is a classic Venezuelan dessert, loved for its light, airy texture and delicate vanilla flavour. This simple yet delicious sponge cake is perfect for enjoying with coffee or tea.",
      flavors: [
        {
          id: 'caramel',
          name: 'Caramel',
          price: '£38'
        },
        {
          id: 'chocolate',
          name: 'Chocolate',
          price: '£40'
        }
      ],
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, butter (MILK), sugar, MILK, vanilla.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring."
        ]
      },
      category: "specialty"
    },
    'cupcakes': {
      name: "Vanilla / Chocolate / Carrot cupcakes",
      price: "£3 each",
      image: cupcakesMix,
      description: "Delicious cupcakes available in vanilla, chocolate, or carrot flavors. Available in boxes of 6 or 12.",
      extraInfo: {
        fillingOptions: "Chocolate, caramel, lotus, ask for other preferences",
        availableBoxes: "6 or 12"
      },
      allergens: {
        ingredients: "EGGS, WHEAT flour, sugar, MILK (including cream, condensed MILK, and evaporated MILK), vanilla.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring."
        ]
      },
      category: "cupcakes"
    },
    'tres-leches-bueno': {
      name: "Kinder Bueno Tres leches cake",
      price: "£9",
      image: cakeBuenos,
      description: "Our traditional tres leches cake enhanced with the irresistible flavor of Kinder Bueno. This exciting variation combines our signature three-milk soaking with the nutty, chocolatey goodness that Bueno lovers adore.",
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, sugar, condensed MILK, evaporated MILK, double cream (MILK), vanilla, NUTS (Bueno filling), cinnamon.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring.",
          "Please consume within 3 days of opening the product."
        ]
      },
      category: "considering"
    },
    'tres-leches-oreo': {
      name: "Oreo Tres leches cake",
      price: "£9",
      image: cakeOreos,
      description: "A cookies and cream dream! Our beloved tres leches cake gets a playful twist with the addition of everyone's favorite chocolate sandwich cookies. Each bite delivers the classic creamy texture of tres leches with delightful crunchy Oreo pieces.",
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, sugar, condensed MILK, evaporated MILK, double cream (MILK), vanilla, Oreo cookies (WHEAT, sugar, palm oil, cocoa), cinnamon.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring.",
          "Please consume within 3 days of opening the product."
        ]
      },
      category: "considering"
    },
    'tres-leches-pistachio': {
      name: "Pistachio Tres leches cake",
      price: "£9",
      image: cakePistachio,
      isNew: true,
      description: "An elegant and sophisticated take on our signature dessert. This luxurious variation incorporates the distinctive flavor and beautiful color of pistachios, creating a tres leches cake that's both visually stunning and incredibly delicious.",
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, sugar, condensed MILK, evaporated MILK, double cream (MILK), vanilla, NUTS (pistachios), cinnamon.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring.",
          "Please consume within 3 days of opening the product."
        ]
      },
      category: "considering"
    },
    'tres-leches-tiramisu': {
      name: "Tiramisu Tres leches cake",
      price: "£9",
      image: cakeTiramisu,
      description: "Our tres leches cake meets the beloved Italian classic! This unique fusion combines the creamy, milk-soaked texture of tres leches with the rich coffee and mascarpone flavors of tiramisu, creating an unforgettable dessert experience.",
      allergens: {
        ingredients: "Whole MILK, EGGS, WHEAT flour, sugar, condensed MILK, evaporated MILK, double cream (MILK), mascarpone (MILK), coffee, vanilla, cinnamon.",
        allergyAdvice: [
          "This product may contain traces or come into contact with NUTS, DAIRY, OR GLUTEN products.",
          "May contain food colouring.",
          "Please consume within 3 days of opening the product."
        ]
      },
      category: "considering"
    }
  };

  const product = productData[productId];

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="product-detail-container">
          <div className="product-breadcrumb">
            <button onClick={() => navigate('/menu')} className="breadcrumb-button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Menu
            </button>
          </div>
          <h1>Product not found</h1>
        </div>
      </div>
    );
  }

  // Helper function to render ingredients with bold allergens
  const renderIngredientsWithBoldAllergens = (text) => {
    const allergenKeywords = ['MILK', 'EGGS', 'WHEAT', 'NUTS', 'DAIRY', 'GLUTEN'];
    const parts = [];
    let lastIndex = 0;

    allergenKeywords.forEach(keyword => {
      const regex = new RegExp(keyword, 'g');
      let match;

      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push({ text: text.substring(lastIndex, match.index), bold: false });
        }
        parts.push({ text: keyword, bold: true, index: match.index });
        lastIndex = match.index + keyword.length;
      }
    });

    // Sort parts by index and remove duplicates
    const sortedParts = parts
      .filter((part, index, self) =>
        index === self.findIndex(p => p.index === part.index)
      )
      .sort((a, b) => (a.index || 0) - (b.index || 0));

    // Rebuild the text with proper ordering
    let result = [];
    let currentIndex = 0;

    text.split('').forEach((char, idx) => {
      const boldPart = sortedParts.find(p => p.index === idx && p.bold);
      if (boldPart) {
        result.push(
          <strong key={`bold-${idx}`}>{boldPart.text}</strong>
        );
        currentIndex = idx + boldPart.text.length;
      } else if (idx >= currentIndex) {
        const nextBoldIndex = sortedParts.find(p => p.index > idx)?.index || text.length;
        const normalText = text.substring(idx, nextBoldIndex);
        if (normalText && !sortedParts.find(p => p.index === idx)) {
          result.push(normalText);
          currentIndex = nextBoldIndex;
        }
      }
    });

    // Simpler approach: use regex to replace
    const pattern = new RegExp(`(${allergenKeywords.join('|')})`, 'g');
    const segments = text.split(pattern);

    return segments.map((segment, index) => {
      if (allergenKeywords.includes(segment)) {
        return <strong key={index}>{segment}</strong>;
      }
      return segment;
    });
  };

  return (
    <div className="product-detail-page">
      {/* Cart Notification Popup */}
      {showCartNotification && (
        <div className="cart-notification">
          <div className="cart-notification-content">
            <h4>Just added to your cart</h4>
            <div className="cart-notification-item">
              <div className="cart-notification-image">
                {product.image ? (
                  <img src={product.image} alt={product.name} />
                ) : (
                  <div className="notification-placeholder">
                    <span>Photo</span>
                  </div>
                )}
              </div>
              <div className="cart-notification-details">
                <span className="notification-product-name">{product.name}</span>
                <span className="notification-quantity">Qty: {quantity}</span>
              </div>
            </div>
            <div className="cart-notification-actions">
              <button
                className="view-cart-button"
                onClick={handleViewCart}
              >
                View Cart ({cartItemCount})
              </button>
              <button
                className="continue-shopping-button"
                onClick={handleContinueShopping}
              >
                Continue Shopping
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="product-detail-container">


        <div className="product-layout">
          <div className="product-image">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="product-photo"
              />
            ) : (
              <div className="image-placeholder">
                <span>Photo Coming Soon</span>
              </div>
            )}
          </div>

          <div className="product-info">
            <div className="product-header">
              <h1 className="product-title">{product.name}</h1>
              <div className="product-price">
                {selectedSize ? selectedSize.price : (selectedFlavor ? selectedFlavor.price : product.price)}
              </div>
            </div>

            {product.isComingSoon && (
              <div className="coming-soon-notice">
                This exciting flavor is currently in development. Stay tuned for updates!
              </div>
            )}

            <div className="product-description">
              <p>{product.description}</p>
            </div>

            {product.sizes && (
              <div className="product-sizes">
                <h3>Available Sizes:</h3>
                <div className="sizes-grid">
                  {product.sizes.map((size) => (
                    <div
                      key={size.id}
                      className={`size-card ${selectedSize?.id === size.id ? 'selected' : ''}`}
                      onClick={() => setSelectedSize(size)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="size-visual"></div>
                      <div className="size-info">
                        <p className="size-dimensions">{size.dimensions}</p>
                        <p className="size-portions">{size.portions}</p>
                      </div>
                      <p className="size-price">{size.price}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {product.flavors && (
              <div className="product-sizes">
                <h3>Available Flavors:</h3>
                <div className="sizes-grid">
                  {product.flavors.map((flavor) => (
                    <div
                      key={flavor.id}
                      className={`size-card ${selectedFlavor?.id === flavor.id ? 'selected' : ''}`}
                      onClick={() => setSelectedFlavor(flavor)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="size-info">
                        <p className="size-dimensions">{flavor.name}</p>
                      </div>
                      <p className="size-price">{flavor.price}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <details className="product-details-collapsible">
              <summary className="details-summary">
                <span className="summary-title">Ingredients & Allergens</span>
                <svg className="chevron-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </summary>
              <div className="details-content">
                <div className="ingredients-section">
                  <h4>Ingredients</h4>
                  <p className="ingredients-text">
                    {renderIngredientsWithBoldAllergens(product.allergens.ingredients)}
                  </p>
                </div>

                <div className="allergy-section">
                  <h4>Allergy Information</h4>
                  <ul className="allergy-advice-list">
                    {product.allergens.allergyAdvice.map((advice, index) => (
                      <li key={index}>
                        <div className="li-content">{renderIngredientsWithBoldAllergens(advice)}</div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="storage-note">
                  <h4>Storage & Handling</h4>
                  <p>All our cakes are made in a kitchen that handles nuts, peanuts, soy, and gluten, so we cannot guarantee they are free from cross-contamination.</p>
                </div>

                <div className="compliance-info">
                  <span className="compliance-badge">Natasha's Law Compliant</span>
                  <a
                    href="https://www.narf.org.uk/what-is-natashas-law"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="learn-more-link"
                  >
                    Learn more
                  </a>
                </div>
              </div>
            </details>

            {/* Order Options */}
            {stripeProduct && (
              <div className="order-options">
                {/* Collection Day */}
                <div className="collection-day-section">
                  <h3>Collection Date</h3>
                  <select
                    value={collectionDay}
                    onChange={(e) => setCollectionDay(e.target.value)}
                    className="collection-day-select"
                  >
                    <option value="">Select a date</option>
                    {collectionDates.map(dateOption => (
                      <option
                        key={dateOption.value}
                        value={dateOption.value}
                        disabled={dateOption.disabled}
                        style={dateOption.isSeparator ? {
                          textAlign: 'center',
                          color: '#ccc',
                          backgroundColor: '#f5f5f5'
                        } : {}}
                      >
                        {dateOption.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantity Selector */}
                <div className="quantity-section">
                  <h3>Quantity</h3>
                  <div className="quantity-selector">
                    <button
                      className="quantity-btn"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <span>−</span>
                    </button>
                    <span className="quantity-display">{quantity}</span>
                    <button
                      className="quantity-btn"
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      <span>+</span>
                    </button>
                  </div>
                </div>

                {/* Personalization Section */}
                <div className="personalization-section">
                  <div className="personalization-header">
                    <input
                      type="checkbox"
                      id="personalization-checkbox"
                      checked={isPersonalized}
                      onChange={handlePersonalizationToggle}
                      className="personalization-checkbox"
                    />
                    <label htmlFor="personalization-checkbox" className="personalization-label">
                      <span className="checkbox-custom"></span>
                      Add personalisation
                    </label>
                  </div>

                  {isPersonalized && (
                    <div className="personalization-options">
                      {/* Custom Message */}
                      <div className="personalization-field">
                        <label htmlFor="personalization-message" className="field-label">
                          Describe your cake requirements (optional)
                        </label>
                        <textarea
                          id="personalization-message"
                          value={personalizationMessage}
                          onChange={(e) => setPersonalizationMessage(e.target.value)}
                          placeholder="E.g., Happy Birthday Sarah, specific colors, decorations, flavours, allergies, or any special requests..."
                          className="personalization-textarea"
                          maxLength={200}
                          rows={3}
                        />
                        <span className="character-count">
                          {personalizationMessage.length}/200 characters
                        </span>
                      </div>

                      {/* Image Upload */}
                      <div className="personalization-field">
                        <label className="field-label">
                          Attach reference images (optional)
                        </label>
                        <div className="file-upload-area">
                          <input
                            type="file"
                            id="personalization-images"
                            multiple
                            accept="image/*"
                            onChange={handleFileUpload}
                            className="file-input"
                            disabled={personalizationImages.length >= 3}
                          />
                          <label htmlFor="personalization-images" className="file-upload-label">
                            <span className="upload-icon">📷</span>
                            <span className="upload-text">
                              {personalizationImages.length >= 3
                                ? 'Maximum 3 images reached'
                                : 'Click to upload images'}
                            </span>
                            <span className="upload-subtext">
                              PNG, JPG up to 5MB each (max 3 images)
                            </span>
                          </label>
                        </div>

                        {/* Image Previews */}
                        {personalizationImages.length > 0 && (
                          <div className="image-previews">
                            {personalizationImages.map(image => (
                              <div key={image.id} className="image-preview">
                                <img src={image.preview} alt={image.name} className="preview-image" />
                                <button
                                  type="button"
                                  onClick={() => removeImage(image.id)}
                                  className="remove-image-btn"
                                  title="Remove image"
                                >
                                  ×
                                </button>
                                <span className="image-name">{image.name}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="personalization-note">
                        <span className="note-icon">ℹ️</span>
                        <span className="note-text">
                          Personalised items may require additional processing time.
                          We'll contact you if we need clarification on your requirements.
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Add to Cart Section */}
                <div className="add-to-cart-section">
                  <button
                    className={`add-to-cart-button ${product.isComingSoon ? 'coming-soon-button' : ''}`}
                    onClick={product.isComingSoon ? () => alert('This flavor is coming soon! We\'ll notify you when it\'s available.') : handleAddToCart}
                    disabled={product.isComingSoon}
                  >
                    {product.isComingSoon ? 'Coming Soon' : `Add to Cart - ${totalPrice?.price}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* You may also like section */}
        <div className="recommendations">
          <h2 className="recommendations-title">You may also like</h2>
          <div className="recommendations-grid">
            {Object.entries(productData)
              .filter(([key]) => key !== productId)
              .sort(() => Math.random() - 0.5)
              .slice(0, 4)
              .map(([key, product]) => (
                <Link key={key} to={`/product/${key}`} className="recommendation-card">
                  <div className="recommendation-image">
                    {product.image ? (
                      <img src={product.image} alt={product.name} />
                    ) : (
                      <div className="recommendation-placeholder">
                        <span>Photo Coming Soon</span>
                      </div>
                    )}
                  </div>
                  <div className="recommendation-info">
                    <h3 className="recommendation-name">{product.name}</h3>
                    <p className="recommendation-price">{product.price}</p>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage; 